import { Injectable } from '@angular/core';
import { Action, State, StateContext, createSelector } from '@ngxs/store';
import { catchError, tap, throwError } from 'rxjs';
import { RequestsType } from 'src/app/shared/types/requests.types';
import { RequestsAction } from './requests.action';
import { RequestsService } from 'src/app/services/requests/requests.service';

@State<RequestsType>({
  name: 'requests',
  defaults: {
    requestList: null,
    requestDetail: null,
    createInfo: null,
    updateInfo: null,
    isLoading: false,
    isUpdateInfoLoading: false,
  },
})
@Injectable()
export class RequestsState {
  static getRequestsList = () =>
    createSelector([RequestsState], (state: RequestsType) => state.requestList);

  static getRequestDetail = () =>
    createSelector(
      [RequestsState],
      (state: RequestsType) => state.requestDetail
    );

  static getCreateInfo = () =>
    createSelector([RequestsState], (state: RequestsType) => state.createInfo);

  static getUpdateInfo = () =>
    createSelector([RequestsState], (state: RequestsType) => state.updateInfo);

  static getIsLoading = () =>
    createSelector([RequestsState], (state: RequestsType) => state.isLoading);

  static getIsUpdateInfoLoading = () =>
    createSelector(
      [RequestsState],
      (state: RequestsType) => state.isUpdateInfoLoading
    );

  constructor(private service: RequestsService) { }

  @Action(RequestsAction.GetRequests)
  getRequestsList(
    ctx: StateContext<RequestsType>,
    action: RequestsAction.GetRequests
  ) {
    ctx.patchState({ requestList: null, isLoading: true });

    return this.service.getRequests().pipe(
      tap((list) => ctx.patchState({ requestList: list, isLoading: false })),
      catchError((error) => {
        ctx.patchState({ isLoading: false });
        return throwError(() => error);
      })
    );
  }

  @Action(RequestsAction.GetRequestDetail)
  detail(
    ctx: StateContext<RequestsType>,
    action: RequestsAction.GetRequestDetail
  ) {
    ctx.patchState({ requestDetail: null, isLoading: true });
    return this.service.getRequestDetail(action.id).pipe(
      tap((data) => {
        ctx.patchState({ requestDetail: data, isLoading: false });
      }),
      catchError((error) => {
        ctx.patchState({ isLoading: false });
        return throwError(() => error);
      })
    );
  }

  @Action(RequestsAction.GetCreateInfo)
  getCreateInfo(
    ctx: StateContext<RequestsType>,
    action: RequestsAction.GetCreateInfo
  ) {
    ctx.patchState({ createInfo: null, isLoading: true });

    return this.service.getCreateInfo().pipe(
      tap((list) => ctx.patchState({ createInfo: list, isLoading: false })),
      catchError((error) => {
        ctx.patchState({ isLoading: false });
        return throwError(() => error);
      })
    );
  }

  @Action(RequestsAction.CreateServiceRequest)
  create(
    ctx: StateContext<RequestsState>,
    action: RequestsAction.CreateServiceRequest
  ) {
    return this.service.createServiceRequest(action.body).pipe();
  }

  @Action(RequestsAction.GetUpdateInfo)
  getUpdateInfo(
    ctx: StateContext<RequestsType>,
    action: RequestsAction.GetUpdateInfo
  ) {
    ctx.patchState({ updateInfo: null, isUpdateInfoLoading: true });

    return this.service.getUpdateInfo(action.id).pipe(
      tap((data) => {
        ctx.patchState({ updateInfo: data, isUpdateInfoLoading: false });
      }),
      catchError((error) => {
        ctx.patchState({ isUpdateInfoLoading: false });
        return throwError(() => error);
      })
    );
  }

  @Action(RequestsAction.UpdateServiceRequest)
  update(
    ctx: StateContext<RequestsType>,
    action: RequestsAction.UpdateServiceRequest
  ) {
    return this.service.updateServiceRequest(action.body);
  }

  @Action(RequestsAction.DeleteRequest)
  deleteRequest(
    ctx: StateContext<RequestsType>,
    action: RequestsAction.DeleteRequest
  ) {
    return this.service.deleteRequest(action.id).pipe(
      tap(() => {
        const state = ctx.getState();
        const updatedList =
          state.requestList?.data?.filter((item) => item.id !== action.id) ?? [];

        ctx.patchState({
          requestList: {
            ...state.requestList,
            data: updatedList,
          }
        });
      }),
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

}
